import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ShoppingBag, Download, Package } from "lucide-react";
import api, { fileUrl } from "../lib/api";
import { GiftFields, EMPTY_GIFT, giftPayload } from "../components/GiftFields";
import { PromoField } from "../components/PromoField";
import { useSeo } from "../lib/seo";
import { ShareButtons } from "../components/ShareButtons";

export default function BundleDetail() {
  const { id } = useParams();
  const [b, setB] = useState(null);
  const [email, setEmail] = useState("");
  const [gift, setGift] = useState(EMPTY_GIFT);
  const [promo, setPromo] = useState(null);
  const [loading, setLoading] = useState(false);
  useSeo({ title: b ? `${b.name} — Bundle, save ${b.discount_pct}%` : "Bundle", description: b ? `${b.description || b.name} Includes ${b.products.map((p) => p.name).join(", ")}. Save $${(b.savings_cents / 100).toFixed(2)} — instant digital download.` : null, path: `/bundle/${id}`,
    jsonLd: b ? { "@context": "https://schema.org", "@type": "Product", name: b.name, description: b.description, offers: { "@type": "Offer", price: (b.price_cents / 100).toFixed(2), priceCurrency: "USD", availability: "https://schema.org/InStock" } } : null });

  useEffect(() => { api.get(`/bundles/${id}`).then((r) => setB(r.data)).catch(() => toast.error("Bundle not found")); }, [id]);

  const buy = async () => {
    if (!email) return toast.error("Please enter your email to receive your downloads.");
    if (gift.enabled && !gift.to) return toast.error("Please enter the recipient's email.");
    setLoading(true);
    try {
      const r = await api.post("/checkout", { bundle_id: id, email, promo_code: promo?.code, ...giftPayload(gift) });
      window.location.assign(r.data.url);
    } catch (e) { toast.error(e?.response?.data?.detail || "Checkout failed"); setLoading(false); }
  };

  if (!b) return <div className="max-w-7xl mx-auto px-6 py-24 text-[#4a4a4a]">Loading…</div>;
  const $ = (c) => `$${(c / 100).toFixed(2)}`;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="bundle-page">
      <div className="grid lg:grid-cols-2 gap-16">
        <div className="grid grid-cols-2 gap-4 content-start">
          {b.products.map((p) => (
            <Link key={p.id} to={`/product/${p.id}`} data-testid={`bundle-item-${p.id}`} className="card-flat overflow-hidden group">
              <div className="aspect-[4/5] bg-[#f4f5f7] overflow-hidden">
                {p.cover_key ? <img src={fileUrl(p.cover_key)} alt={p.name} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" /> : <div className="w-full h-full flex items-center justify-center font-serif text-4xl text-[#4a4a4a]">{p.name?.[0]}</div>}
              </div>
              <div className="p-3">
                <div className="overline text-[#4a4a4a]">{p.category}</div>
                <div className="font-serif text-lg text-[#0a192f] leading-tight mt-1">{p.name}</div>
                <div className="text-xs text-[#4a4a4a] mt-1 line-through">{$(p.price_cents)}</div>
              </div>
            </Link>
          ))}
        </div>
        <div>
          <div className="overline text-[#d4af37] flex items-center gap-2"><Package className="w-4 h-4" /> Bundle · save {b.discount_pct}%</div>
          <h1 data-testid="bundle-title" className="font-serif text-5xl mt-4 text-[#0a192f] leading-tight tracking-tight">{b.name}</h1>
          <p className="text-lg text-[#4a4a4a] mt-6 leading-relaxed">{b.description}</p>
          <ul className="mt-6 space-y-2 text-[#1a1a1a]">
            {b.products.map((p) => <li key={p.id} className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-[#d4af37]" /> {p.name}</li>)}
          </ul>
          <div className="divider-gold my-10" />
          <div className="flex items-end gap-4">
            <div className="text-4xl font-serif text-[#0a192f]" data-testid="bundle-price">{$(promo ? promo.final_cents : b.price_cents)}</div>
            <div className="text-lg text-[#4a4a4a] line-through mb-1" data-testid="bundle-full-price">{$(b.full_price_cents)}</div>
            <div className="bg-[#d4af37] text-[#0a192f] text-xs font-bold px-2 py-1 mb-2 tracking-wider" data-testid="bundle-savings">YOU SAVE {$(b.savings_cents)}</div>
          </div>
          <div className="mt-8">
            <label className="overline text-[#4a4a4a]">Your email (for download delivery)</label>
            <input data-testid="bundle-buy-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="mt-2 w-full border border-[#e5e5e5] bg-white px-4 py-3 outline-none focus:border-[#0a192f]" />
          </div>
          <PromoField bundleId={id} promo={promo} setPromo={setPromo} />
          <GiftFields gift={gift} setGift={setGift} />
          <button data-testid="bundle-buy-btn" onClick={buy} disabled={loading} className="btn-navy mt-6 inline-flex items-center gap-2 disabled:opacity-50">
            <ShoppingBag className="w-4 h-4" /> {loading ? "Redirecting…" : `${gift.enabled ? "Gift" : "Buy"} the bundle · ${$(promo ? promo.final_cents : b.price_cents)}`}
          </button>
          <div className="mt-6"><ShareButtons url={window.location.href} title={b.name} description={b.description} image={b.products[0]?.cover_key ? fileUrl(b.products[0].cover_key) : null} testPrefix="bundle-share" /></div>
          <p className="text-xs text-[#4a4a4a] mt-4 flex items-center gap-2"><Download className="w-3.5 h-3.5" /> Instant delivery of all {b.products.length} files. Secure links valid 14 days.</p>
        </div>
      </div>
    </div>
  );
}
